(() => {
  const pricingCache = new Map();

  const moneyFmt = (n) => {
    // n is dollars (number)
    const v = Number(n || 0);
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(v);
  };

  const toCents = (dollars) => Math.round(Number(dollars || 0) * 100);

  const norm = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/&quot;|"/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const getProp = (props, keys) => {
    if (!props) return "";
    // exact match first
    for (const k of keys) if (props[k] != null && String(props[k]).trim() !== "") return String(props[k]).trim();
    // case-insensitive fallback
    const entries = Object.entries(props);
    for (const k of keys) {
      const kn = norm(k);
      const hit = entries.find(([kk, vv]) => norm(kk) === kn && String(vv).trim() !== "");
      if (hit) return String(hit[1]).trim();
    }
    return "";
  };

  async function fetchPricing(handle) {
    const map = window.PACKORA_PRICING_MAP || {};
    const url = map[handle];
    if (!url) return null;
    if (pricingCache.has(url)) return pricingCache.get(url);

    const p = fetch(url, { cache: "no-store" })
      .then((r) => r.json())
      .catch(() => null);

    pricingCache.set(url, p);
    return p;
  }

  function tryNested(obj, path) {
    let cur = obj;
    for (const key of path) {
      if (!cur || typeof cur !== "object") return null;
      cur = cur[key];
    }
    return cur ?? null;
  }

  function findClosestQtyKey(qty, availableKeys) {
    const nums = availableKeys
      .map((k) => Number(String(k).replace(/[^\d.]/g, "")))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b);

    if (!nums.length) return null;
    const q = Number(qty);

    // prefer next >= q, else last
    const next = nums.find((n) => n >= q);
    return String(next ?? nums[nums.length - 1]);
  }

  function lookupPrice(pricing, cfg) {
    if (!pricing) return null;

    const size = cfg.size;
    const material = cfg.material;
    const sides = cfg.sides;
    const qty = String(cfg.qty);

    // 1) Common nested object form:
    // pricing[size][material][sides][qty] => { unit_price, subtotal } OR number
    const direct =
      tryNested(pricing, [size, material, sides, qty]) ??
      tryNested(pricing, [size, material, qty]) ??
      tryNested(pricing, [size, sides, qty]) ??
      tryNested(pricing, [size, qty]) ??
      null;

    const parseVal = (val) => {
      if (val == null) return null;
      if (typeof val === "number") return { unit: val, subtotal: val * Number(cfg.qty) };
      if (typeof val === "string") {
        const n = Number(val.replace(/[^\d.]/g, ""));
        if (Number.isFinite(n)) return { unit: n, subtotal: n * Number(cfg.qty) };
      }
      if (typeof val === "object") {
        const unit =
          val.unit_price ?? val.unit ?? val.price_per_unit ?? val.ppu ?? val["Price per unit"] ?? val["unitPrice"];
        const sub = val.subtotal ?? val.total ?? val.line_total ?? val["Config subtotal"] ?? val["subtotal_price"];
        const unitN =
          typeof unit === "number" ? unit : Number(String(unit || "").replace(/[^\d.]/g, ""));
        const subN =
          typeof sub === "number" ? sub : Number(String(sub || "").replace(/[^\d.]/g, ""));

        if (Number.isFinite(unitN) && Number.isFinite(subN)) return { unit: unitN, subtotal: subN };
        if (Number.isFinite(unitN)) return { unit: unitN, subtotal: unitN * Number(cfg.qty) };
        if (Number.isFinite(subN)) return { unit: subN / Number(cfg.qty), subtotal: subN };
      }
      return null;
    };

    let parsed = parseVal(direct);
    if (parsed) return parsed;

    // 2) If the exact qty key doesn't exist, try snapping to the closest available qty under the best branch
    // Try to locate the “qty map” object and pick closest key.
    const qtyMap =
      tryNested(pricing, [size, material, sides]) ??
      tryNested(pricing, [size, material]) ??
      tryNested(pricing, [size, sides]) ??
      tryNested(pricing, [size]) ??
      null;

    if (qtyMap && typeof qtyMap === "object") {
      const closest = findClosestQtyKey(cfg.qty, Object.keys(qtyMap));
      if (closest != null) {
        parsed = parseVal(qtyMap[closest]);
        if (parsed) {
          // IMPORTANT: parsed might represent that snapped qty.
          // We still want subtotal to reflect the snapped tier’s subtotal (most pricing tables do).
          return parsed;
        }
      }
    }

    // 3) Array rows fallback
    if (Array.isArray(pricing)) {
      const row = pricing.find((r) => {
        const rs = norm(r.size ?? r.Size);
        const rm = norm(r.material ?? r.Material);
        const rps = norm(r.sides ?? r["Printed Sides"] ?? r["printed_sides"]);
        const rq = String(r.qty ?? r.quantity ?? r.Quantity ?? "");
        return (
          (!size || rs === norm(size)) &&
          (!material || rm === norm(material)) &&
          (!sides || rps === norm(sides)) &&
          rq === String(cfg.qty)
        );
      });
      return parseVal(row);
    }

    return null;
  }

  async function getCart() {
    const r = await fetch("/cart.js", { cache: "no-store" });
    return r.json();
  }

  function renderSections(resp) {
    if (!resp || !resp.sections) return false;

    const parser = new DOMParser();

    const replaceSection = (id, selector) => {
      const html = resp.sections[id];
      if (!html) return;
      const doc = parser.parseFromString(html, "text/html");
      const newNode = doc.querySelector(selector);
      const curNode = document.querySelector(selector);
      if (newNode && curNode) curNode.innerHTML = newNode.innerHTML;
    };

    // Dawn uses these selectors
    replaceSection("main-cart-items", "#main-cart-items .js-contents");
    replaceSection("main-cart-footer", "#main-cart-footer .js-contents");

    return true;
  }

  async function changeLine({ line, quantity, properties }) {
    const body = {
      line,
      quantity,
      properties,
      sections: ["main-cart-items", "main-cart-footer"],
      sections_url: window.location.pathname
    };

    const r = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });

    if (!r.ok) throw new Error("cart change failed");
    return r.json();
  }

  async function recalcAndUpdateLine(line, newQty) {
    const cart = await getCart();
    const item = cart.items[line - 1];
    if (!item) return;

    const handle = item.handle;
    const props = item.properties || {};

    const cfg = {
      qty: Number(newQty),
      size: getProp(props, ["Packora - Size", "Packora Size", "Size (Interior dimensions)", "Size", "size", "Size:"]),
      material: getProp(props, ["Packora - Material", "Packora Material", "Material", "material", "Material:"]),
      sides: getProp(props, [
        "Packora - Printed sides",
        "Packora - Printed Sides",
        "Printed sides",
        "Printed Sides",
        "printed_sides",
        "Sides",
        "sides"
      ]),
      finish: getProp(props, ["Packora - Finish", "Finish", "finish"]),
      lamination: getProp(props, ["Packora - Lamination", "Lamination", "lamination"]),
      shape: getProp(props, ["Shape", "shape"]),
      corners: getProp(props, ["Corners", "corners"]),
      labelType: getProp(props, ["Label Type", "Label Style", "label type", "label style"])
    };

    const pricing = await fetchPricing(handle);
    const found = lookupPrice(pricing, cfg);

    if (!found) {
      // If not found, still update qty so cart works, but don't overwrite pricing
      const resp = await changeLine({ line, quantity: cfg.qty, properties: props });
      renderSections(resp);
      return;
    }

    const unit = Number(found.unit);
    const subtotal = Number(found.subtotal);

    const nextProps = {
      ...props,

      "Packora - Quantity": String(cfg.qty),
      "_pk_qty": String(cfg.qty),

      "Packora - Unit Price": moneyFmt(unit),
      "_pk_unit_cents": String(toCents(unit)),

      "Packora - Subtotal": moneyFmt(subtotal),
      "_pk_subtotal_cents": String(toCents(subtotal))
    };

    const resp = await changeLine({ line, quantity: cfg.qty, properties: nextProps });
    renderSections(resp);
  }

  // Capture BEFORE Dawn cart.js so we don’t get the “error updating cart” UI when we’re doing our own update.
  document.addEventListener(
    "change",
    (e) => {
      const input = e.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.matches('input[name="updates[]"]')) return;

      const line = Number(input.getAttribute("data-index"));
      const qty = Number(input.value);

      if (!Number.isFinite(line) || !Number.isFinite(qty)) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      recalcAndUpdateLine(line, qty).catch(() => {
        // fallback
        window.location.reload();
      });
    },
    true
  );

  document.addEventListener(
    "click",
    (e) => {
      const a = e.target.closest("cart-remove-button a");
      if (!a) return;

      const wrapper = a.closest("cart-remove-button");
      const line = Number(wrapper?.getAttribute("data-index") || wrapper?.id?.replace("Remove-", ""));
      if (!Number.isFinite(line)) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      recalcAndUpdateLine(line, 0).catch(() => window.location.reload());
    },
    true
  );
})();